import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {createRoot} from 'react-dom/client';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {useComponentProps} from 'helpers/hooks/utils';
import StyledHydratePage from 'components/pages/Dev/Hydrate/HydratePage/HydratePage.styles';
import StyleProvider from 'components/organisms/Providers/StyleProvider/StyleProvider';
import ThemeProvider from 'components/organisms/Providers/ThemeProvider/ThemeProvider';
import Table from 'components/organisms/Tables/Table/Table';
import Box from 'components/atoms/Layout/Box/Box';
import Typography from 'components/atoms/Text/Typography/Typography';
import LinearProgress from 'components/atoms/Progress/LinearProgress/LinearProgress';
import {useHydrationWalk, useHydrationGenerate, reportGenerateError} from 'helpers/hooks/hydrate';
import {useSearchParams} from 'react-router-dom';

// A mount can throw when a component's recorded props are out of date - render null for it and
// report it so the page can list which ones. `skip` short-circuits the other propsets of a
// component that already failed, so one stale component throws once, not once per propset. The
// report is deferred out of the error commit so it does not re-enter React mid-recovery. The sink
// root is detached from the router, so a throw only ever nulls its own entry.
class GenerateBoundary extends React.Component {
  constructor (props) {
    super(props);
    this.state = {failed: false};
  }

  static getDerivedStateFromError () {
    return {failed: true};
  }

  componentDidCatch (error) {
    const name = this.props.name;
    const message = error?.message ?? `${error}`;
    window.queueMicrotask(() => {
      this.props.onError(name, message);
    });
  }

  render () {
    return (this.state.failed || this.props.skip) ? null : this.props.children;
  }
}

// A plain span as the cell: the default cell (TableText -> TextTableCell) is built on the action
// system (useActionItem -> auth + router), which the isolated sink has no context for. The table
// chrome we are after (MuiTable / TableCell / TableRow / pagination / sort-label) generates from
// the table rendering its rows regardless of what the cell returns, so a hook-free span is enough.
const HydrateTableCell = (cellProps) => {
  return <span>{cellProps.cell?.getValue?.()}</span>
};

const HYDRATE_TABLE_COLUMNS = [
  {id: 'name', accessorKey: 'name', header: 'Name', Cell: HydrateTableCell},
  {id: 'stage', accessorKey: 'stage', header: 'Stage', Cell: HydrateTableCell},
  // Center-aligned, like the numeric / tasks columns in the real table: a center header and body
  // cell (text-align:center, justify-content:center) and, sortable + centered, the sort label's
  // padding-left offset that keeps the label centered against the icon.
  {id: 'score', accessorKey: 'score', header: 'Score', Cell: HydrateTableCell,
    muiTableHeadCellProps: {align: 'center'}, muiTableBodyCellProps: {align: 'center'}},
  // Right-aligned, like the numeric / currency columns in the real tables (e.g. TeamMembersTable):
  // a right head + body cell (text-align:right, justify-content:right) and, sortable + right, the
  // header's flex-direction:row-reverse that flips the sort icon to the other side of the label.
  {id: 'revenue', accessorKey: 'revenue', header: 'Revenue', Cell: HydrateTableCell,
    muiTableHeadCellProps: {align: 'right'}, muiTableBodyCellProps: {align: 'right'}},
  {id: 'owner', accessorKey: 'owner', header: 'Owner', Cell: HydrateTableCell},
  {id: 'updated', accessorKey: 'updated', header: 'Updated', Cell: HydrateTableCell}
];

const HYDRATE_TABLE_DATA = [
  {id: 1, name: 'Alpha', stage: 'Seed', score: 82, revenue: 1200000, owner: 'Ann', updated: '2026-01-04'},
  {id: 2, name: 'Beta', stage: 'Series A', score: 74, revenue: 850000, owner: 'Bo', updated: '2026-02-11'},
  {id: 3, name: 'Gamma', stage: 'Growth', score: 91, revenue: 3400000, owner: 'Cy', updated: '2026-03-19'}
];

// The state the real EntitiesTable always carries, so the table chrome that only exists in those
// states generates here too. A bare table (no selection, no pinning, no sort) covers the plain
// cell but never the variants a real collection renders on first mount - and those are exactly
// what injects, one frame at a time, on the first switch back to a list after a profile reload:
//   - columnPinning left + two right -> box-shadow:-4px…inset on the last left-pinned cell,
//     4px…inset on the first (edge) right-pinned cell, and the non-edge right-pinned variant
//     (right:var(--mrt-right), no shadow) from the second right column
//   - listSelection -> the checkbox column (MuiCheckbox) and the pinned mrt-row-select column;
//     one selected row + count:1 also exercises the checked cell and the indeterminate select-all
//   - two opposite sorts + enableSorting -> MuiTableSortLabel active in both directions (the
//     descending icon is transform:rotate(180deg)) plus the multi-sort order-number MuiBadge
//   - the EntitiesTable chrome flags (resizing / dragging / actions) -> the resize handle
//     (MuiDivider), the drag grip and the column-actions MuiIconButton
// columnState must carry columnOrder: getColumnOrderCopy spreads it once a column is missing.
const HYDRATE_TABLE_COLUMN_STATE = {
  columnOrder: ['name', 'stage', 'score', 'revenue', 'owner', 'updated'],
  columnPinning: {left: ['name'], right: ['score', 'updated']},
  columnVisibility: {},
  columnSizing: {}
};

const HYDRATE_TABLE_LIST_STATE = {
  sort: [{id: 'name', desc: false}, {id: 'score', desc: true}],
  filter: [],
  search: '',
  pagination: {pageIndex: 0, pageSize: HYDRATE_TABLE_DATA.length}
};

const HYDRATE_TABLE_LIST_SELECTION = {
  rowSelection: {1: true},
  count: 1
};

// A row click handler makes the rows 'clickable', which is what gives the body cells cursor:pointer
// and the :hover outline - the shape a real list, whose rows open a profile, always renders.
const HYDRATE_TABLE_ROW_CLICK = () => {};

// A full, real table render for the sink. The manifest replay div-swaps our styled table
// components, so the css that only exists inside a rendered table subtree never generates: the MUI
// table internals (MuiTable / TableCell / TableRow / TableHead / TableBody / TablePagination /
// TableSortLabel) and MRT's own anonymous <Box sx> rules. Rendering one Table injects that whole
// subtree. It is rendered directly (not as a $__generate__ entry), so it is NOT div-swapped.
// Virtualization stays on (the app's real path, so the css matches), which is why the sink
// container is sized and visibility:hidden - MRT needs a real viewport to mount its rows.
const HydrateTable = () => {
  return <Table columns={HYDRATE_TABLE_COLUMNS} data={HYDRATE_TABLE_DATA}
                rowCount={HYDRATE_TABLE_DATA.length} dataKey="id"
                columnState={HYDRATE_TABLE_COLUMN_STATE}
                listState={HYDRATE_TABLE_LIST_STATE}
                listSelection={HYDRATE_TABLE_LIST_SELECTION}
                onRowClick={HYDRATE_TABLE_ROW_CLICK}
                enableSorting={true}
                enableStickyHeader={true}
                enableColumnActions={true}
                enableColumnResizing={true}
                enableColumnDragging={true}
                enableColumnOrdering={true}
                enablePinning={true}/>
};

// The generate sink. Mounted once into a detached React root (see HydratePage), it renders every
// recorded component so its css lands in the sheet, ready for Capture. It wraps the entries in the
// app's own StyleProvider and ThemeProvider, so the styling environment is identical to a real
// render - same sheet, same theme (the active white label is baked into themeLight / themeDark at
// import). The div-swap ($__generate__) means each entry renders as a bare div or a MUI leaf, so
// no router / AuthProvider / data is needed. It subscribes to the generate state itself and grows
// its own batches. LocalizationProvider is here because styled(DatePicker) renders the real MUI X
// picker (not div-swapped), which reads the localization context DateField normally supplies.
const HydrateSink = () => {
  const generate = useHydrationGenerate();

  const failed = new Set(generate.errors.map((error) => {
    return error.name;
  }));

  return <StyleProvider>
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {/* Rendered from the start, not gated on completion: MRT mounts over several frames, so
            it needs the whole run to settle before the teardown on 'done' unmounts the sink. */}
        <GenerateBoundary name="(table)" skip={failed.has('(table)')} onError={reportGenerateError}>
          <HydrateTable/>
        </GenerateBoundary>
        {generate.list.slice(0, generate.rendered).map((entry, index) => {
          return <GenerateBoundary key={index} name={entry.name}
                                   skip={failed.has(entry.name)}
                                   onError={reportGenerateError}>
            <entry.Component {...entry.props}/>
          </GenerateBoundary>
        })}
      </LocalizationProvider>
    </ThemeProvider>
  </StyleProvider>
};

// The walk keeps its state outside React, so subscribing here shows the same run the toolbar is
// driving - no context between the two.
const HydratePage = (props) => {
  const innerProps = useComponentProps(props, 'HydratePage');

  const hydrationWalk = useHydrationWalk();
  const generate = useHydrationGenerate();
  const [searchParams, setSearchParams] = useSearchParams();

  const walked = hydrationWalk.steps.reduce((total, step) => {
    return total + step.rules;
  }, 0);

  // Generate reloads the page with ?generate=1 so it boots on a fresh sheet (no payload on
  // /dev/hydrate), then renders every recorded component into a hidden sink - display:none still
  // mounts, so styled-components still generates and Capture reads the full sheet. The param is
  // cleared as soon as it starts, so a refresh does not launch it again; the view then follows
  // the generate status.
  const start = generate.start;
  const shouldStart = searchParams.get('generate') === '1';

  useEffect(() => {
    if (shouldStart) {
      start();
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete('generate');
        return next;
      }, {replace: true});
    }
  }, [shouldStart, start, setSearchParams]);

  const generating = shouldStart || generate.status !== 'idle';

  // The sink mounts once into a separate React root, not this tree: the div-swap ($__generate__)
  // means every recorded component renders as a bare div or a MUI leaf, so it needs no router and
  // no AuthProvider. HydrateSink subscribes to the generate state itself and grows its own batches,
  // so it is rendered a single time here and never re-pushed.
  //
  // The container is sized and visibility:hidden, not display:none: the fake table (HydrateTable)
  // virtualizes, so it needs a real viewport height to mount its rows. visibility:hidden keeps the
  // layout box while hiding it; position:fixed and pointer-events:none keep it out of the way.
  const rootRef = useRef(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);

  const teardown = () => {
    if (rootRef.current) {
      // Deferred: unmounting one root synchronously inside another's commit warns in React 18.
      // The css already lives in the sheet (styled-components keeps rules past unmount), so
      // Capture still reads the full payload after the sink is gone.
      const root = rootRef.current;
      const container = containerRef.current;
      const overlay = overlayRef.current;
      rootRef.current = null;
      containerRef.current = null;
      overlayRef.current = null;
      window.queueMicrotask(() => {
        root.unmount();
        container?.remove();
        overlay?.remove();
      });
    }
  };

  useEffect(() => {
    if (generating && generate.status !== 'done' && !containerRef.current) {
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '1280px';
      container.style.height = '900px';
      container.style.overflow = 'hidden';
      container.style.visibility = 'hidden';
      container.style.pointerEvents = 'none';
      container.style.zIndex = '-1';
      container.setAttribute('aria-hidden', 'true');
      document.body.appendChild(container);
      containerRef.current = container;

      // MUI portals its modals and poppers to document.body, so a dialog in the sink lands
      // outside the hidden container and blacks the page out for the length of the run. A plain
      // <style> hides them while it lasts: the rules still generate, because styled-components
      // injects from the render and not from what is on screen. A DOM element, deliberately, and
      // not createGlobalStyle - it must never reach the sheet Capture reads.
      const overlay = document.createElement('style');
      overlay.textContent = 'body > .MuiModal-root, body > .MuiPopper-root ' +
        '{visibility: hidden !important; pointer-events: none !important;}';
      document.head.appendChild(overlay);
      overlayRef.current = overlay;
      rootRef.current = createRoot(container);
      rootRef.current.render(<HydrateSink/>);
    }
  }, [generating, generate.status]);

  // On 'done' the whole list has mounted and injected, so tear the sink down: it leaves portalled
  // modals (open dialogs, drawers) on document.body, which a display:none container cannot hide.
  useEffect(() => {
    if (generate.status === 'done') {
      teardown();
    }
  }, [generate.status]);

  useEffect(() => {
    return () => {
      teardown();
    };
  }, []);

  const percent = generate.total > 0 ? Math.round((generate.rendered / generate.total) * 100) : 0;
  const failed = generate.errors.length > 0 ? ` — ${generate.errors.length} outdated` : '';

  let generateLabel = `Generating ${generate.rendered} / ${generate.total}${failed}`;
  if (generate.status === 'loading') {
    generateLabel = 'Loading components…';
  } else if (generate.status === 'done') {
    generateLabel = `Generated ${generate.total} components — press Capture${failed}`;
  }

  return <StyledHydratePage {...innerProps}>
    {generating ? <React.Fragment>
      <Typography variant="subtitle2">{generateLabel}</Typography>
      <LinearProgress variant="determinate" value={percent}/>
      {generate.errors.map((error, index) => {
        return <Box key={index} className="HydratePage-row HydratePage-failed">
          <Typography variant="body2">{error.name}</Typography>
          <Typography variant="body2">{error.message}</Typography>
        </Box>
      })}
    </React.Fragment> : <React.Fragment>
      {hydrationWalk.steps.map((step, index) => {
        return <Box key={index} className={`HydratePage-row${step.error ? ' HydratePage-failed' : ''}`}>
          <Typography variant="body2">{step.label}</Typography>
          <Typography variant="body2" className="HydratePage-path">{step.path}</Typography>
          <Typography variant="body2">
            {step.error ?? Object.keys(step.themes).map((name) => {
              return `${name} ${step.themes[name]}`;
            }).join('  ·  ')}
          </Typography>
          <Typography variant="body2">{step.error ? 'failed' : `+${step.rules}`}</Typography>
        </Box>
      })}
      {(hydrationWalk.steps.length > 0) ? <Box className="HydratePage-row HydratePage-total">
        <Typography variant="subtitle2">{`${hydrationWalk.steps.length} steps`}</Typography>
        <Typography variant="subtitle2"/>
        <Typography variant="subtitle2"/>
        <Typography variant="subtitle2">{`+${walked}`}</Typography>
      </Box> : null}
      {(hydrationWalk.steps.length === 0) ? <Typography variant="body2">
        Pick a collection, then Walk. Capture copies the sheet to the clipboard.
      </Typography> : null}
    </React.Fragment>}
  </StyledHydratePage>
};

HydratePage.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default HydratePage;
